import { useState } from "react";
import { useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { Modal, ModalBody } from "reactstrap";
import { stripePromise } from "../../../stripe";
import CardForm from "./card-form";
import axiosInstance from "../../../axios-instance";
import { isTrialEligible, trialPaymentCopy } from "../../../utils/trialAccess";

/* ─── Design tokens ──────────────────────────────────────────────────────── */
const C = {
  orange:      "#FE9920",
  orangeLight: "#FFF7ED",
  orangeBorder:"#FED7AA",
  dark:        "#111827",
  mid:         "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
  border:      "#E5E7EB",
  surface:     "#F9FAFB",
  green:       "#10B981",
  greenBg:     "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenDark:   "#065F46",
};

/* ─── Inline-responsive style injection ─────────────────────────────────── */
const MODAL_STYLES = `
  .sco-modal .modal-dialog  { max-width: 900px; }
  .sco-modal .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 24px 64px rgba(0,0,0,0.14);
    overflow: hidden;
  }
  .sco-modal .modal-body    { padding: 0; }

  .sco-layout {
    display: flex;
    min-height: 520px;
  }
  .sco-left  { flex: 0 0 55%; padding: 36px 36px 32px; background: #fff; }
  .sco-right {
    flex: 0 0 45%;
    padding: 36px 32px 32px;
    background: #F9FAFB;
    border-left: 1px solid #E5E7EB;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 720px) {
    .sco-modal .modal-dialog { max-width: 100%; margin: 0; }
    .sco-modal .modal-content { border-radius: 0; min-height: 100dvh; }
    .sco-layout               { flex-direction: column; min-height: auto; }
    .sco-left                 { padding: 24px 20px 20px; }
    .sco-right {
      border-left: none;
      border-top: 1px solid #E5E7EB;
      order: -1;
      padding: 20px;
    }
  }
`;

/* ─── Order Summary (right column) ─────────────────────────────────────── */
const OrderSummary = ({ selectedPlan, planPeriodType, price, discount, total, appliedCoupon, trialEligible }) => {
  const periodLabel  = planPeriodType === "monthly" ? "Monthly"  : "Annual";
  const periodSuffix = planPeriodType === "monthly" ? "/mo"      : "/yr";
  const trialCopy = trialPaymentCopy(trialEligible);
  const dueToday = trialEligible ? "0.00" : Number(total).toFixed(2);
  const renewalText  = trialEligible
    ? `After 14 days you’ll be charged $${Number(total).toFixed(2)}${periodSuffix} unless you cancel`
    : (planPeriodType === "monthly"
      ? `Renews monthly at $${price.toFixed(2)} unless cancelled`
      : `Renews annually at $${price.toFixed(2)} unless cancelled`);

  return (
    <>
      {/* Label */}
      <p style={{ margin: "0 0 18px", fontSize: "11px", fontWeight: 700, color: C.faint, textTransform: "uppercase", letterSpacing: "1px" }}>
        Order Summary
      </p>

      {/* Plan card */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: 700, color: C.dark }}>
              {selectedPlan?.plan_name ?? "—"}
            </p>
            <span style={{
              display: "inline-block",
              fontSize: "11px",
              fontWeight: 600,
              color: C.orange,
              background: C.orangeLight,
              border: `1px solid ${C.orangeBorder}`,
              borderRadius: "20px",
              padding: "2px 10px",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}>
              {periodLabel}
            </span>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: "12px" }}>
            <p style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: C.dark, lineHeight: 1.1 }}>
              ${price.toFixed(2)}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: "12px", color: C.faint }}>{periodSuffix}</p>
          </div>
        </div>
      </div>

      <hr style={{ border: "none", borderTop: `1px solid ${C.border}`, margin: "0 0 18px" }} />

      {/* Price breakdown */}
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: C.muted, marginBottom: "10px" }}>
          <span>Subtotal</span>
          <span>${price.toFixed(2)}</span>
        </div>

        {discount > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "14px", color: C.green, marginBottom: "10px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{
                fontSize: "10px",
                fontWeight: 700,
                background: C.greenBg,
                color: C.greenDark,
                border: `1px solid ${C.greenBorder}`,
                borderRadius: "20px",
                padding: "1px 7px",
                textTransform: "uppercase",
              }}>
                {appliedCoupon?.type === "P"
                  ? `${appliedCoupon.value}% OFF`
                  : "DISCOUNT"}
              </span>
              Coupon applied
            </span>
            <span>−${discount.toFixed(2)}</span>
          </div>
        )}

        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "16px",
          fontWeight: 700,
          color: C.dark,
          paddingTop: "12px",
          borderTop: `1px solid ${C.border}`,
        }}>
          <span>{trialCopy.dueTodayLabel}</span>
          <span>${dueToday}</span>
        </div>
      </div>

      {/* Renewal + Stripe trust */}
      <div style={{ marginTop: "24px" }}>
        <p style={{ margin: "0 0 12px", fontSize: "12px", color: C.faint, lineHeight: 1.6 }}>
          {renewalText}
          {trialEligible && appliedCoupon
            ? " Your coupon applies to that first charge, not today."
            : ""}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {/* Shield icon */}
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M10 2L4 5v5c0 4.418 2.686 8.166 6 9.5C13.314 18.166 16 14.418 16 10V5l-6-3z"
              stroke={C.faint}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{ fontSize: "12px", color: C.faint }}>Payments processed securely by Stripe</span>
        </div>
      </div>
    </>
  );
};

/* ─── Modal ─────────────────────────────────────────────────────────────── */
const CardInfoModal = (props) => {
  const { selectedPlan, planPeriodType, setShowCardInfoModal, getUserAndPlans } = props;
  const userPlan = useSelector((state) => state.subscription?.userPlan);
  const trialEligible = isTrialEligible(userPlan);
  const trialCopy = trialPaymentCopy(trialEligible);

  /* Coupon state — owned here so OrderSummary and CardForm share it */
  const [couponInput,    setCouponInput]    = useState("");
  const [appliedCoupon,  setAppliedCoupon]  = useState(null);
  const [couponLoading,  setCouponLoading]  = useState(false);
  const [couponError,    setCouponError]    = useState("");
  const [couponOpen,     setCouponOpen]     = useState(false);

  const toggle = () => setShowCardInfoModal(false);

  /* Price computation */
  const price = planPeriodType === "monthly"
    ? parseFloat(selectedPlan?.monthly_price ?? 0)
    : parseFloat(selectedPlan?.annual_price  ?? 0);

  const discount = appliedCoupon
    ? appliedCoupon.type === "P"
      ? parseFloat(((price * appliedCoupon.value) / 100).toFixed(2))
      : Math.min(parseFloat(appliedCoupon.value), price)
    : 0;

  const total = Math.max(0, parseFloat((price - discount).toFixed(2)));

  /* Coupon handlers */
  const handleApplyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponLoading(true);
    setCouponError("");
    setAppliedCoupon(null);
    try {
      const res = await axiosInstance.get(`/admin/subscription/validate-coupon/${code}`);
      if (res.data?.success) {
        setAppliedCoupon(res.data.coupon);
        setCouponOpen(false);
      } else {
        setCouponError(res.data?.message || "Invalid or expired coupon code.");
      }
    } catch {
      setCouponError("Could not validate coupon. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
    setCouponOpen(false);
  };

  return (
    <>
      <style>{MODAL_STYLES}</style>

      <Modal
        className="sco-modal billing_modal"
        isOpen={true}
        toggle={toggle}
        centered
      >
        <ModalBody>
          <div className="sco-layout">

            {/* ── Left: Payment Form ─────────────────────────────── */}
            <div className="sco-left">

              {/* Header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: "19px", fontWeight: 700, color: C.dark, lineHeight: 1.3 }}>
                    {trialCopy.heading}
                  </h4>
                  <p style={{ margin: "5px 0 0", fontSize: "13px", color: C.muted }}>
                    {trialCopy.subheading}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggle}
                  aria-label="Close payment modal"
                  style={{
                    flexShrink: 0,
                    background: "none",
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                    color: C.muted,
                    fontSize: "18px",
                    lineHeight: 1,
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "12px",
                    marginTop: "2px",
                    transition: "background 0.15s",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Stripe Elements wraps CardForm */}
              <Elements stripe={stripePromise} options={{ appearance: { theme: "stripe" } }}>
                <CardForm
                  getUserAndPlans={getUserAndPlans}
                  onClose={toggle}
                  selectedPlan={selectedPlan}
                  planPeriodType={planPeriodType}
                  couponCode={appliedCoupon?.code ?? ""}
                  price={price}
                  discount={discount}
                  total={total}
                  couponInput={couponInput}
                  setCouponInput={setCouponInput}
                  appliedCoupon={appliedCoupon}
                  couponLoading={couponLoading}
                  couponError={couponError}
                  couponOpen={couponOpen}
                  setCouponOpen={setCouponOpen}
                  onApplyCoupon={handleApplyCoupon}
                  onRemoveCoupon={handleRemoveCoupon}
                />
              </Elements>
            </div>

            {/* ── Right: Order Summary ───────────────────────────── */}
            <div className="sco-right">
              <OrderSummary
                selectedPlan={selectedPlan}
                planPeriodType={planPeriodType}
                price={price}
                discount={discount}
                total={total}
                appliedCoupon={appliedCoupon}
                trialEligible={trialEligible}
              />
            </div>

          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default CardInfoModal;
